import React, { useCallback, useState, useEffect } from "react";
import { Box, Grid, Typography, useMediaQuery } from "@mui/material";
import { useTranslation } from "react-i18next";
import AdditionalCard from "../../components/reports/additionalCard";
import StatusCard from "../../components/reports/statusCard";
import RiskCard from "../../components/reports/riskCard/index";
import RiskModal from "../../components/reports/riskModal";
import { criticalData } from "./critical_format";
import { useDispatch, useSelector } from "react-redux";
import { requestContactExpert } from "../../redux/actions/reportsActionCreator";
import SadAvatar from "../../components/shared/sadAvatar";
import { useParams, useLocation } from "react-router-dom";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import moment from "moment";
import ControlApiKey from "./ControlApiKey";
import { requestConsultReport } from "../../redux/actions/reportsActionCreator";
import { translate } from "../../utils/utils";

function Report() {
  const isMobile = useMediaQuery("(max-width:900px)");

  const [criticalContext, setCriticalContext] = useState({});
  const [selectedRisk, setSelectedRisk] = useState({});
  const [open, setOpen] = useState(false);
  const [apiKey, setApiKey] = useState("");

  const handleClose = () => setOpen(false);

  const { reportId } = useParams();

  const routesWithoutShowAnswers = [`/consult-report/${reportId}`];

  const { pathname } = useLocation();
  const shouldIncludeShowAnswers = !routesWithoutShowAnswers.includes(pathname);

  const { isAuth } = useSelector((state) => state.user);

  const dispatch = useDispatch();
  const params = useParams();
  const mdUp = useMediaQuery((theme) => theme.breakpoints.up("md"), {
    defaultMatches: true,
    noSsr: false,
  });
  const lgUp = useMediaQuery((theme) => theme.breakpoints.up("lg"), {
    defaultMatches: true,
    noSsr: false,
  });
  const reportData = useSelector((state) => state.reports.selected);

  const handleSelectedRisk = (el) => {
    setSelectedRisk(el);
    setOpen(true);
  };

  const handleApiKey = (key) => {
    setApiKey(key);
  };

  const contactExpert = (url) => {
    dispatch(requestContactExpert(url));
  };

  const setterCriticalContext = useCallback(
    (score) => {
      switch (true) {
        case score >= 9:
          setCriticalContext(criticalData["critical"]);
          break;
        case score < 9 && score >= 7:
          setCriticalContext(criticalData["very_high"]);
          break;
        case score < 7 && score >= 5:
          setCriticalContext(criticalData["high"]);
          break;
        case score < 5 && score >= 3:
          setCriticalContext(criticalData["moderate"]);
          break;
        case score < 3:
          setCriticalContext(criticalData["acceptable"]);
          break;
        default:
          break;
      }
    },
    [setCriticalContext]
  );

  const getFormattedeDate = (isoDate) => {
    if (isoDate) {
      const date = new Date(isoDate);
      return (
        date.getDate() + "/" + (date.getMonth() + 1) + "/" + date.getFullYear()
      );
    } else {
      return "";
    }
  };

  useEffect(() => {
    setterCriticalContext(reportData.total_score);
  }, [reportData.total_score, setterCriticalContext]);

  useEffect(() => {
    if (apiKey) {
      dispatch(requestConsultReport(params.reportId, apiKey));
    }
  }, [dispatch, apiKey]);

  const { t } = useTranslation();

  return Object.values(reportData).length === 0 ? (
    <ControlApiKey handleApiKey={handleApiKey} />
  ) : (
    <div style={{ display: "flex", height: "calc(100vh - 64px)" }}>
      {Object.keys(reportData).length
        ? (!isMobile || (isMobile && !isAuth)) && (
            <Grid
              container
              spacing={2}
              justifyContent="center"
              sx={{
                height: isAuth ? `100vh` : `calc(100vh-64px)`,
                overflow: "auto",
              }}
            >
              <Grid
                item
                lg={isAuth ? 11 : 10}
                xs={isAuth ? 11 : 12}
                style={
                  (!isAuth && mdUp) || (isAuth && lgUp)
                    ? {
                        background:
                          "linear-gradient(180deg, white 0%, white 90%, transparent 100%)",
                        position: "sticky",
                        top: "0px",
                      }
                    : {}
                }
              >
                <div
                  style={{
                    padding: !isAuth ? "0px 30px" : "0px",
                    marginTop: "30px",
                  }}
                >
                  {isAuth ? (
                    <div>
                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <Typography variant="body4">
                            {t("report_id")}:{" "}
                            <Typography
                              variant="body4"
                              sx={{ fontSize: "15px", fontWeight: 400 }}
                            >
                              {reportData.uuid}
                            </Typography>
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body4">
                            {t("quiz_version")}:{" "}
                            <Typography
                              variant="body4"
                              sx={{ fontSize: "15px" }}
                            >
                              {reportData.version}
                            </Typography>
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography
                            variant="title2"
                            sx={{ fontSize: "16px" }}
                          >
                            {reportData.quiz_title}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body4">
                            {t("submission_date")}:{" "}
                            <Typography
                              variant="body4"
                              sx={{ fontSize: "15px", fontWeight: 400 }}
                            >
                              {moment(reportData.date).format("DD MMMM YYYY")}
                            </Typography>
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2">
                            <i>{reportData[translate("quiz_description")]}</i>
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          {reportData?.instance_id &&
                            shouldIncludeShowAnswers && (
                              <Typography
                                variant="body4"
                                sx={{ fontSize: "15px" }}
                              >
                                <a
                                  id="link"
                                  href={`${process.env.REACT_APP_ORIGIN_URL}/completed_quizzes/${reportData.instance_id}`}
                                  target="_blank"
                                >
                                  <OpenInNewIcon sx={{ fontSize: "17px" }} />{" "}
                                  {t("show_answers")}
                                </a>
                              </Typography>
                            )}
                        </Grid>
                      </Grid>
                    </div>
                  ) : (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: mdUp ? "row" : "column",
                        justifyContent: "space-between",
                      }}
                    >
                      <div style={{ display: "flex", flexDirection: "column" }}>
                        <Typography variant="body4">
                          {t("display_name_A")}:{" "}
                          <Typography
                            variant="body4"
                            sx={{ fontSize: "15px", fontWeight: 400 }}
                          >
                            {reportData.organization_name}
                          </Typography>
                        </Typography>
                        <Typography variant="body4">
                          {t("quiz_title")}:{" "}
                          <Typography
                            variant="body4"
                            sx={{ fontSize: "15px", fontWeight: 400 }}
                          >
                            {reportData.quiz_title}
                          </Typography>
                        </Typography>
                        <Typography variant="body4">
                          {t("quiz_description")}:{" "}
                          <Typography variant="body2">
                            <i>{reportData[translate("quiz_description")]}</i>
                          </Typography>
                        </Typography>
                      </div>
                      <div>
                        <Typography
                          style={{ display: "inline-block" }}
                          variant="subtitle2"
                        >
                          {t("report_id")}:
                        </Typography>{" "}
                        <Typography
                          style={{ display: "inline-block" }}
                          variant="body2"
                        >
                          {reportData.uuid}
                        </Typography>{" "}
                        <div>
                          <Typography
                            style={{ display: "inline-block" }}
                            variant="subtitle2"
                          >
                            Date:
                          </Typography>{" "}
                          <Typography
                            style={{ display: "inline-block" }}
                            variant="body2"
                          >
                            {getFormattedeDate(reportData.date)}
                          </Typography>
                        </div>
                      </div>
                    </div>
                  )}
                  <br></br>
                  <Grid container spacing={2}>
                    <Grid item xl={6} lg={6} md={isAuth ? 12 : 6} xs={12}>
                      <StatusCard
                        total_score={reportData.total_score}
                        criticalContext={criticalContext}
                        requestContactExpert={contactExpert}
                        uuid={reportData.uuid}
                        removeIt={true}
                      />
                    </Grid>
                    <Grid item xl={6} lg={6} md={isAuth ? 12 : 6} xs={12}>
                      <AdditionalCard
                        Estimated_Probability={
                          reportData.Estimated_Probability_of_successful_attack
                        }
                        pourcentage_of_affected_assets={
                          reportData.pourcentage_of_affected_assets
                        }
                        pourcentage_of_high_or_higher_risks={
                          reportData.pourcentage_of_high_or_higher_risks
                        }
                      />
                    </Grid>
                  </Grid>
                </div>
              </Grid>

              <Grid item lg={isAuth ? 11 : 10} xs={isAuth ? 11 : 12}>
                <div style={{ padding: !isAuth ? "0px 30px" : "0px" }}>
                  <div>
                    <Typography variant="title2">
                      {t("risk_assessment")}
                    </Typography>
                  </div>
                  <br></br>

                  <Box>
                    <Grid container spacing={2}>
                      {reportData.Risks.map((el) => (
                        <Grid
                          item
                          xs={12}
                          onClick={() => handleSelectedRisk(el)}
                          key={el.id}
                        >
                          <RiskCard risk={el} />
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                </div>
              </Grid>
            </Grid>
          )
        : !isMobile && (
            <Box
              sx={{
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
                display: "flex",
                flexDirection: "column",
                textAlign: "center",
                padding: isAuth ? "30px" : "30px 300px",
                height: isAuth ? "calc(100vh - 60px)" : "calc(100vh - 124px)",
                overflow: "auto",
              }}
            >
              <SadAvatar label="there is no reports yet !!" alt="NO REPORTS" />
            </Box>
          )}

      {open && (
        <RiskModal
          open={open}
          selectedRisk={selectedRisk}
          handleClose={handleClose}
        />
      )}
    </div>
  );
}

export default Report;
